import * as React from "react";
import { isSearchExcluded } from "./seo/visibility";
import {
  CANONICAL_ORIGIN,
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  canonicalUrl,
  DEFAULT_TITLE,
  SEO_OG,
  SITE_NAME,
  absoluteAssetUrl,
} from "./seo/site";

export type PageSEOOptions = {
  /** 페이지 제목 (document.title, og:title 등) */
  title: string;
  /** 메타 설명 (160자 이내 권장) */
  description: string;
  /** canonical 및 og:url에 사용할 경로 (예: /workmate/exam-score). 비우면 현재 location.pathname 사용 */
  path?: string;
  /** 검색 키워드 (선택) */
  keywords?: string;
  /** noindex 시 true (기본: index, follow) */
  noindex?: boolean;
  /** Open Graph 이미지 URL (선택, 공유 시 미리보기 이미지) */
  ogImage?: string;
  /** JSON-LD 구조화 데이터 (선택) */
  jsonLd?: object;
  /** 페이지 타입 (Article, WebPage 등, 기본: WebPage) */
  pageType?: "WebPage" | "Article" | "Product" | "Service";
  /** 게시일 (Article 타입일 때) */
  datePublished?: string;
  /** 수정일 (Article 타입일 때) */
  dateModified?: string;
  /** 작성자 이름 (Article 타입일 때) */
  authorName?: string;
  /** Breadcrumb 경로 (선택) */
  breadcrumbs?: Array<{ name: string; url: string }>;
};

function setOrCreateMeta(
  attr: "name" | "property",
  attrValue: string,
  content: string
) {
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setOrCreateLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function stripMarkdownLite(text: string): string {
  return text
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function generateBreadcrumbSchema(breadcrumbs: Array<{ name: string; url: string }>, baseUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url.startsWith("http") ? crumb.url : `${baseUrl}${crumb.url}`,
    })),
  };
}

export function generateArticleSchema(
  title: string,
  description: string,
  url: string,
  datePublished?: string,
  dateModified?: string,
  authorName?: string,
  imageUrl?: string
) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url,
  };

  if (datePublished) schema.datePublished = datePublished;
  if (dateModified) schema.dateModified = dateModified;
  if (authorName) {
    schema.author = {
      "@type": "Person",
      name: authorName,
    };
  }
  if (imageUrl) schema.image = imageUrl;

  return schema;
}

export function generateFAQPageSchema(items: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: stripMarkdownLite(item.q),
      acceptedAnswer: {
        "@type": "Answer",
        text: stripMarkdownLite(item.a),
      },
    })),
  };
}

export function generateHowToSchema(
  name: string,
  description: string,
  steps: Array<{ title: string; body: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    step: steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: stripMarkdownLite(step.title),
      text: stripMarkdownLite(step.body),
    })),
  };
}

export function usePageSEO(options: PageSEOOptions) {
  const {
    title,
    description,
    path,
    keywords,
    noindex = false,
    ogImage,
    jsonLd,
    pageType = "WebPage",
    datePublished,
    dateModified,
    authorName,
    breadcrumbs,
  } = options;
  const baseUrl = CANONICAL_ORIGIN;
  const pathname = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const url = canonicalUrl(pathname);

  React.useEffect(() => {
    const section = pathname.split("/")[1] as keyof typeof SEO_OG;
    const imageUrl = absoluteAssetUrl(ogImage || SEO_OG[section] || SEO_OG.home, baseUrl);
    const isBrandedImage = Object.values(SEO_OG).some((asset) => imageUrl === absoluteAssetUrl(asset));
    let finalJsonLd = jsonLd;

    if (!finalJsonLd) {
      if (pageType === "Article" && (datePublished || authorName)) {
        finalJsonLd = generateArticleSchema(
          title,
          description,
          url,
          datePublished,
          dateModified,
          authorName,
          imageUrl
        );
      } else {
        finalJsonLd = {
          "@context": "https://schema.org",
          "@type": pageType,
          name: title,
          description,
          url,
          inLanguage: "ko-KR",
          isPartOf: { "@type": "WebSite", name: SITE_NAME, url: CANONICAL_ORIGIN },
          image: imageUrl,
        };
      }
    }

    const jsonLdArray: unknown[] = [];
    if (finalJsonLd) {
      jsonLdArray.push(finalJsonLd);
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      jsonLdArray.push(generateBreadcrumbSchema(breadcrumbs, baseUrl));
    }

    const finalJsonLdToUse =
      jsonLdArray.length === 1 ? jsonLdArray[0] : jsonLdArray.length > 1 ? jsonLdArray : undefined;
    document.title = title;

    setOrCreateMeta("name", "description", description);

    setOrCreateMeta("name", "keywords", keywords || DEFAULT_KEYWORDS);

    setOrCreateLink("canonical", url);

    const ogTags: [string, string][] = [
      ["og:type", pageType === "Article" ? "article" : "website"],
      ["og:title", title],
      ["og:description", description],
      ["og:url", url],
      ["og:site_name", SITE_NAME],
      ["og:locale", "ko_KR"],
      ["og:image", imageUrl],
      ["og:image:alt", title],
    ];
    ogTags.forEach(([property, content]) => {
      setOrCreateMeta("property", property, content);
    });

    // User uploads (book covers, article photos) do not share our card dimensions/type.
    for (const [property, content] of [["og:image:width", "1200"], ["og:image:height", "630"], ["og:image:type", "image/png"]]) {
      if (isBrandedImage) setOrCreateMeta("property", property, content);
      else document.querySelector(`meta[property="${property}"]`)?.remove();
    }
    for (const [property, content] of [["article:published_time", datePublished], ["article:modified_time", dateModified]] as const) {
      if (pageType === "Article" && content) setOrCreateMeta("property", property, content);
      else document.querySelector(`meta[property="${property}"]`)?.remove();
    }

    const twitterTags: [string, string][] = [
      ["twitter:card", "summary_large_image"],
      ["twitter:title", title],
      ["twitter:description", description],
      ["twitter:image", imageUrl],
      ["twitter:image:alt", title],
    ];
    twitterTags.forEach(([name, content]) => {
      setOrCreateMeta("name", name, content);
    });

    setOrCreateMeta("name", "robots", (noindex || isSearchExcluded(pathname)) ? "noindex, follow" : "index, follow, max-image-preview:large");

    if (finalJsonLdToUse) {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-page-seo]');
      existingScripts.forEach((script) => script.remove());

      if (Array.isArray(finalJsonLdToUse)) {
        finalJsonLdToUse.forEach((ld, index) => {
          const script = document.createElement("script");
          script.setAttribute("type", "application/ld+json");
          script.setAttribute("data-page-seo", `true-${index}`);
          script.textContent = JSON.stringify(ld);
          document.head.appendChild(script);
        });
      } else {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("data-page-seo", "true");
        script.textContent = JSON.stringify(finalJsonLdToUse);
        document.head.appendChild(script);
      }
    } else {
      const existing = document.querySelectorAll('script[type="application/ld+json"][data-page-seo]');
      existing.forEach((script) => script.remove());
    }

    return () => {
      document.title = DEFAULT_TITLE;
      setOrCreateMeta("name", "description", DEFAULT_DESCRIPTION);
      setOrCreateMeta("name", "keywords", DEFAULT_KEYWORDS);
      setOrCreateMeta("name", "robots", isSearchExcluded(window.location.pathname) ? "noindex, follow" : "index, follow, max-image-preview:large");
      setOrCreateLink("canonical", canonicalUrl(window.location.pathname));
      for (const [property, content] of [
        ["og:type", "website"], ["og:title", DEFAULT_TITLE], ["og:description", DEFAULT_DESCRIPTION],
        ["og:url", canonicalUrl(window.location.pathname)], ["og:image", absoluteAssetUrl(SEO_OG.home)],
        ["og:image:alt", DEFAULT_TITLE], ["og:image:width", "1200"], ["og:image:height", "630"], ["og:image:type", "image/png"],
      ]) setOrCreateMeta("property", property, content);
      for (const [name, content] of [["twitter:title", DEFAULT_TITLE], ["twitter:description", DEFAULT_DESCRIPTION],
        ["twitter:image", absoluteAssetUrl(SEO_OG.home)], ["twitter:image:alt", DEFAULT_TITLE]]) setOrCreateMeta("name", name, content);
      document.querySelectorAll('meta[property^="article:"]').forEach((meta) => meta.remove());
      const scripts = document.querySelectorAll('script[type="application/ld+json"][data-page-seo]');
      scripts.forEach((script) => script.remove());
    };
  }, [
    title,
    description,
    url,
    pathname,
    keywords,
    noindex,
    ogImage,
    jsonLd === undefined ? undefined : JSON.stringify(jsonLd),
    pageType,
    datePublished,
    dateModified,
    authorName,
    breadcrumbs === undefined ? undefined : JSON.stringify(breadcrumbs),
    baseUrl,
  ]);
}
